import React, { useMemo, useCallback, useState, useEffect } from "react";
import { Container, Typography, Box, Grid, Divider } from "@mui/material";
import Navbar from "../../Navbar";
import { useFechas } from "../pedidosEnvasado/context/FechaContext";
import { usePedidosFacturados } from "./hooks/usePedidosFacturados";
import { StatCard } from "./components/StatCard";
import { FilterBar } from "./components/FilterBar";
import { TableSkeleton } from "./components/TableSkeleton";
import { ErrorAlert } from "./components/ErrorAlert";
import { createColumns, createTableConfig } from "./utils/tableConfig";
import { TABLE_INITIAL_STATE } from "./constants";
import AnularFactura from "./anularFactura/AnularFactura";
import { verEnviarPDFenvasado } from "../../../services/services";

const MaterialReactTable = React.lazy(() => import("material-react-table"));

function HistorialFacturados() {
  const { desde, hasta, setDesde, setHasta } = useFechas();
  const { pedidos, loading, error, estadisticas, obtenerPedidos, setError } = usePedidosFacturados();
  
  const [anularFactura, setAnularFactura] = useState(false);
  const [rowFactura, setRowFactura] = useState(null);

  const columns = useMemo(() => createColumns(), []);

  // Carga inicial al montar el componente
  useEffect(() => {
    obtenerPedidos(desde, hasta);
  }, []); // Solo se ejecuta una vez al montar

  const handleActualizar = useCallback(() => {
    obtenerPedidos(desde, hasta);
  }, [obtenerPedidos, desde, hasta]);

  const handleAnularFactura = useCallback((row) => {
    setAnularFactura(true);
    setRowFactura(row);
  }, []);

  const handleCloseAnular = useCallback(() => {
    setAnularFactura(false);
    setRowFactura(null);
    obtenerPedidos(desde, hasta);
  }, [obtenerPedidos, desde, hasta]);

  const handleVerFactura = useCallback((row) => {
    if (row.id_factura) {
      verEnviarPDFenvasado(row.id_factura, 0);
    } else {
      setError("Esta factura no está disponible");
    }
  }, [setError]);

  const handleVerNC = useCallback((row) => {
    if (row.id_nc) {
      verEnviarPDFenvasado(row.id_nc, 0);
    }
  }, []);

  const tableHandlers = useMemo(() => ({
    handleVerFactura,
    handleVerNC,
    handleAnularFactura
  }), [handleVerFactura, handleVerNC, handleAnularFactura]);

  const tableConfig = useMemo(() => 
    createTableConfig(columns, pedidos, tableHandlers, TABLE_INITIAL_STATE),
    [columns, pedidos, tableHandlers]
  );

  const renderTable = () => {
    if (loading && pedidos.length === 0) {
      return <TableSkeleton />;
    }

    if (error) {
      return <ErrorAlert error={error} onClose={() => setError(null)} />;
    }

    return (
      <React.Suspense fallback={<TableSkeleton />}>
        <MaterialReactTable {...tableConfig} />
      </React.Suspense>
    );
  };

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <Navbar />
      
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 }, py: 3 }}>

        {/* Stats cards que ocupan todo el ancho pero son más bajas */}
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={4}>
            <StatCard title="Total" value={estadisticas.totalFacturas} subtitle="pedidos" />
          </Grid>
          <Grid item xs={4}>
            <StatCard title="Facturados" value={estadisticas.facturadas} subtitle="pedidos" />
          </Grid>
          <Grid item xs={4}>
            <StatCard title="Anulados" value={estadisticas.anuladas} subtitle="pedidos" />
          </Grid>
        </Grid>

        <FilterBar
          desde={desde}
          hasta={hasta}
          setDesde={setDesde}
          setHasta={setHasta}
          onActualizar={handleActualizar}
          loading={loading}
        />

        {renderTable()}

        {anularFactura && rowFactura && (
          <AnularFactura
            open={anularFactura}
            setOpen={setAnularFactura}
            factura={rowFactura}
            onClose={handleCloseAnular}
          />
        )}
      </Container>
    </Box>
  );
}

export default HistorialFacturados;